import { Hyperlink } from '@databases/types/database';
import { AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';

import { serializeError } from '../../utils';
import { CommonRootState, loadAsync } from '../redux';

import {
  selectHyperlinkOptional,
  selectHyperlinkRequired,
  selectHyperlinkStatus,
} from './selectors';
import hyperlinksSlice from './slice';

type Dispatcher<Result> = ThunkAction<Result, CommonRootState, void, AnyAction>;

/**
 * Load the specified hyperlink into the store. If the hyperlink is already loaded, no request is made.
 */
export const loadHyperlink =
  (
    url: string,
    {
      forceReload = false,
    }: {
      forceReload?: boolean;
    } = {}
  ): Dispatcher<Promise<Hyperlink>> =>
  async (dispatch, getState) => {
    await loadAsync(
      () => selectHyperlinkStatus(getState(), { url }),
      () => {
        dispatch(hyperlinksSlice.actions.loadHyperlinkPending({ url }));
      },
      async () =>
        (!forceReload && selectHyperlinkOptional(getState(), { url })) || { url },
      async (hyperlink) => {
        dispatch(addHyperlinkToList(hyperlink));
        dispatch(hyperlinksSlice.actions.loadHyperlinkFinished({ url }));
      },
      (error) => {
        dispatch(
          hyperlinksSlice.actions.loadHyperlinkError({
            url,
            error: serializeError(error),
          })
        );
      },
      { forceReload }
    );

    return selectHyperlinkRequired(getState(), { url });
  };

/**
 * Add a new hyperlink to the list of hyperlinks. If a hyperlink with the same URL is already loaded, it is replaced.
 */
export const addHyperlinkToList = (hyperlink: Hyperlink): AnyAction =>
  addHyperlinksToList([hyperlink]);

export const addHyperlinksToList = (hyperlinks: Hyperlink[]): AnyAction =>
  hyperlinksSlice.actions.addHyperlinksToList({
    hyperlinks,
  });

/**
 * Delete a hyperlink from the list of hyperlinks.
 */
export const deleteHyperlinkFromList = (hyperlink: Hyperlink | string): AnyAction =>
  deleteHyperlinksFromList([hyperlink]);

export const deleteHyperlinksFromList = (
  hyperlinks: Array<Hyperlink | string>
): AnyAction =>
  hyperlinksSlice.actions.deleteHyperlinksFromList({
    urls: hyperlinks.map((hyperlink) =>
      typeof hyperlink === 'string' ? hyperlink : hyperlink.url
    ),
  });
